import {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {API_URL} from "../../../../config";
import {isSignInExpired} from "./RsoSignInExpired";

const endpoint = `${API_URL}/Dashboard/api/RsoPropertyForecast/workspace`;
const empty = {data:null,error:"",status:"loading",version:0};

// The dashboard and its open editor share this owner. Closing the editor therefore
// uses the workspace it just refreshed instead of starting the same request again.
export default function useRsoWorkspace({token,month,enabled=true,refreshKey}) {
  const scope = `${token}:${month}`;
  const active = enabled && !!token && !!month;
  const current = useRef({scope,active,generation:0});
  if (current.current.scope !== scope || current.current.active !== active)
    current.current = {scope,active,generation:current.current.generation+1};
  const generation = current.current.generation;
  const alive = useRef(true);
  const inFlight = useRef(null);
  const [state,setState] = useState({...empty,scope:null});
  useEffect(() => {alive.current=true;return () => {alive.current=false;};},[]);
  const refresh = useCallback((options = {}) => {
    const ownsScope = () => alive.current && current.current.active && current.current.scope === scope
      && current.current.generation === generation;
    if (!ownsScope()) return Promise.resolve(false);
    const previous = inFlight.current;
    if (previous?.scope === scope && previous.generation === generation) {
      if (!options.afterChange) return previous.promise;
      // A read begun before a committed write cannot confirm that write. Queue one
      // fresh read after it, shared by all callers waiting on the same change.
      if (!previous.followUp) previous.followUp = previous.promise.then(() => ownsScope() ? refresh() : false);
      return previous.followUp;
    }
    const request = {scope,generation,promise:null,followUp:null};
    inFlight.current = request;
    setState((old) => ({...(old.scope === scope ? old : empty),scope,error:"",status:"loading"}));
    request.promise = (async () => {
      try {
        const {data} = await axios.get(endpoint,{headers:{Authorization:`Bearer ${token}`},
          params:{forecastMonth:`${month}-01`},timeout:30000});
        if (!Array.isArray(data?.properties)) throw new Error("Incomplete workspace response");
        if (!ownsScope() || inFlight.current !== request) return false;
        setState((old) => ({scope,data,error:"",status:"ready",version:old.version+1}));
        return true;
      } catch (failure) {
        if (!ownsScope() || inFlight.current !== request) return false;
        const forbidden = failure?.response?.status === 403;
        const message = failure?.response?.data?.message;
        setState((old) => ({scope,data:null,status:"failed",version:old.version,
          error:"Could not load saved progress. Retry to see your latest property statuses.",
          forbidden,forbiddenMessage:forbidden && typeof message === "string" ? message.trim() : "",
          signInExpired:isSignInExpired(failure)}));
        return false;
      } finally {
        if (inFlight.current === request) inFlight.current = null;
      }
    })();
    return request.promise;
  },[scope,token,month,generation]);
  useEffect(() => {if (active) refresh();},[active,refresh,refreshKey]);
  return {...(active && state.scope === scope ? state : empty),scope,refresh};
}
